import React, {useCallback, useEffect, useRef, useState} from "react";

import {
    Alert,
    Button,
    Card,
    Checkbox,
    Collapse,
    DatePicker,
    Empty,
    Flex,
    message,
    notification,
    Popconfirm,
    Progress,
    Select,
    Spin,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import {
    CameraOutlined,
    CheckCircleOutlined,
    CheckOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    DownloadOutlined,
    InfoCircleOutlined,
    PlusOutlined,
    ReloadOutlined,
    SaveOutlined,
    StopOutlined,
    SyncOutlined,
    ThunderboltOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import Apiurl from "../ConstURL";
import AsinReportProgress, {
    CANCEL_PENDING_HINT,
    fmtDuration,
    jobDurationSec,
    ZORO_FAILED_HINT,
} from "./AsinReportProgress";

dayjs.extend(utc);

const {Text, Title} = Typography;

// Пока идёт сравнение или снимается копия — опрашиваем часто (на панели живой ход работы), иначе — раз в 30 с.
// Опросы идут цепочкой (следующий — после ответа на предыдущий), два запроса одновременно не висят.
const POLL_MS = 2000;
const POLL_RETRY_MS = 6000; // после неудачного запроса
const IDLE_POLL_MS = 30000;
// Вкладка в фоне: редкий опрос, только чтобы заголовок вкладки показал «готово».
const HIDDEN_POLL_MS = 30000;
const LOAD_TIMEOUT_MS = 15000;
// После «Проверить и снять копию» проверка идёт в фоне: опрашиваем часто, пока не увидим её итог (но не дольше).
const SNAPSHOT_WATCH_MS = 90000;

const FILE_GONE_HINT = "файл удалён — хранятся 10 последних отчётов за 30 дней";
const REPLACED_HINT = "за этот день было несколько загрузок — хранится последняя";

const isZeroTime = (iso) => !iso || String(iso).startsWith("0001");
// Дата копии — календарный день "YYYY-MM-DD": берём из строки, без сдвига часового пояса.
const fmtDay = (day) => (isZeroTime(day) ? "—" : `${day.slice(8, 10)}.${day.slice(5, 7)}.${day.slice(0, 4)}`);
const fmtDayShort = (day) => (isZeroTime(day) ? "—" : `${day.slice(8, 10)}.${day.slice(5, 7)}`);
const fmtMsk = (iso) => (isZeroTime(iso) ? "" : dayjs(iso).utcOffset(180).format("DD.MM.YYYY HH:mm") + " МСК");
const fmtMskShort = (iso) => (isZeroTime(iso) ? "" : dayjs(iso).utcOffset(180).format("DD.MM HH:mm") + " МСК");
const fmtPair = (a, b) => `${fmtDay(a)} → ${fmtDay(b)}`;

const intFmt = new Intl.NumberFormat("ru-RU");
const fmtInt = (v) => intFmt.format(Number(v) || 0);

// Русское множественное число: plural(5, "строка", "строки", "строк") -> "строк".
const plural = (n, one, few, many) => {
    const a = Math.abs(Math.trunc(Number(n) || 0)) % 100;
    const b = a % 10;
    if (a > 10 && a < 20) return many;
    if (b === 1) return one;
    if (b >= 2 && b <= 4) return few;
    return many;
};
const fmtRows = (n) => `${fmtInt(n)} ${plural(n, "строка", "строки", "строк")}`;
const fmtUploads = (n) => `${fmtInt(n)} ${plural(n, "загрузка", "загрузки", "загрузок")} подряд`;

// Магазин в списке: «Nygmat (NY)» — имя и коды файлов All List; файл: «Nygmat (NY)» или только код, если не сопоставлен.
const shopLabel = (s) => {
    const codes = Array.isArray(s.Codes) ? s.Codes.filter(Boolean) : [];
    return codes.length > 0 ? `${s.Name} (${codes.join(", ")})` : s.Name || s.Id;
};
// У несопоставленного файла сервер отдаёт вместо магазина сам код — тогда только код.
const fileLabel = (f) => (f.Shop && f.Shop !== f.Code ? `${f.Shop} (${f.Code})` : f.Code);

// Одинаковый набор id без учёта порядка.
const sameSet = (a, b) => {
    if (a.length !== b.length) return false;
    const set = new Set(a);
    return b.every((x) => set.has(x));
};

const isActive = (j) => j.Status === "queued" || j.Status === "running";

// Задача для живой панели: самая свежая из идущих, иначе самая свежая из ожидающих в очереди.
const pickPanelJob = (list) => {
    const newest = (status) => list.reduce((best, j) => {
        if (j.Status !== status) return best;
        if (!best) return j;
        return (Date.parse(j.CreatedAt) || 0) > (Date.parse(best.CreatedAt) || 0) ? j : best;
    }, null);
    return newest("running") || newest("queued");
};

// Копии — от новой к старой.
const sortSnapshots = (list) =>
    (Array.isArray(list) ? list : [])
        .filter((s) => s && typeof s.Date === "string")
        .sort((x, y) => (x.Date < y.Date ? 1 : x.Date > y.Date ? -1 : 0));

const downloadHref = (r) =>
    Apiurl + "/allistcompare/file?" + new URLSearchParams({token: localStorage.getItem("token"), id: r.Id, v: r.FinishedAt || ""});

const STATUS = {
    queued: {color: "default", label: "в очереди", icon: <ClockCircleOutlined/>},
    running: {color: "processing", label: "формируется", icon: <SyncOutlined spin/>},
    done: {color: "green", label: "готов", icon: <CheckCircleOutlined/>},
    failed: {color: "red", label: "ошибка", icon: <CloseCircleOutlined/>},
    cancelled: {color: "default", label: "отменён", icon: <StopOutlined/>},
};

// Ключ галочки -> поле Config в ответе сервера.
const OPTIONS = [
    {key: "zoro", cfg: "Zoro", label: "Данные Zoro", tip: "Zoro SKU, Title, Quantity, Handling time, Brand"},
    {key: "warehouse", cfg: "Warehouse", label: "Warehouse Portal", tip: "WP SKU и Quantity (сопоставление по Zoro SKU)"},
];

// Счётчики живой панели для задачи сравнения (формат — как у счётчиков AsinReportProgress).
const compareCounters = (job) => {
    const s = job.Stats || {};
    return [
        {key: "deleted", title: "Удалено ASIN", value: s.Deleted, stage: "compare", tip: `строк: ${fmtInt(s.DeletedRows)}`},
        {key: "zeroed", title: "Обнулено ASIN", value: s.Zeroed, stage: "compare", tip: `строк: ${fmtInt(s.ZeroedRows)}`},
        job.WithWarehouse && {
            key: "warehouse",
            title: "Есть в WP",
            value: s.WarehouseMatched,
            stage: "warehouse",
            tip: `строк, найденных в Warehouse Portal; в наличии: ${fmtInt(s.WarehouseInStock)}`,
        },
        job.WithZoro && {
            key: "zoro",
            title: "Zoro найдено",
            value: s.ZoroFound,
            stage: "zoro",
            tip: (
                <>
                    <div>не найдено: {fmtInt(s.ZoroNotFound)} · сбой: {fmtInt(s.ZoroFailed)} · из кэша: {fmtInt(s.ZoroCached)}</div>
                    <div style={{opacity: 0.75, marginTop: 4}}>{ZORO_FAILED_HINT}</div>
                </>
            ),
        },
    ].filter(Boolean);
};

const cardStyle = {width: "100%", maxWidth: 1100, borderRadius: 16, border: "1px solid #2a2140"};
const wrapStyle = {width: "100%", boxSizing: "border-box", padding: "24px 28px"};
const numStyle = {whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums"};

// latestByCode — файлы самой новой копии (Latest) по коду: для неё в подсказке показываем «не меняется с …».
const makeSnapshotColumns = (latestByCode, newestDay) => [
    {
        title: "Дата",
        dataIndex: "Date",
        render: (v, r) => (
            <Flex gap="6px" align="center" wrap="wrap">
                <Text strong style={{whiteSpace: "nowrap"}}>{fmtDay(v)}</Text>
                {Number(r.Replaced) > 0 && (
                    <Tooltip title={REPLACED_HINT}>
                        <Tag color="gold" style={{marginInlineEnd: 0, cursor: "help"}}>
                            заменена {fmtInt(r.Replaced)} {plural(r.Replaced, "раз", "раза", "раз")}
                        </Tag>
                    </Tooltip>
                )}
            </Flex>
        ),
    },
    {
        title: "Загружено в manager",
        dataIndex: "LoggedAt",
        render: (v) => <span style={{whiteSpace: "nowrap"}}>{fmtMsk(v) || "—"}</span>,
    },
    {
        title: "Копия снята",
        dataIndex: "TakenAt",
        render: (v) => <span style={{whiteSpace: "nowrap"}}>{fmtMsk(v) || "—"}</span>,
    },
    {
        title: "Строк",
        dataIndex: "Rows",
        align: "right",
        render: (v) => <span style={numStyle}>{fmtInt(v)}</span>,
    },
    {
        title: "ASIN",
        dataIndex: "Asins",
        align: "right",
        render: (v) => <span style={numStyle}>{fmtInt(v)}</span>,
    },
    {
        title: "Файлов",
        dataIndex: "Files",
        align: "right",
        render: (files, r) => {
            const list = Array.isArray(files) ? files : [];
            if (list.length === 0) return <span style={numStyle}>0</span>;
            const inactive = list.filter((f) => f.Inactive).length;
            const isNewest = r.Date === newestDay;
            return (
                <Flex gap="6px" align="center" justify="flex-end">
                    {inactive > 0 && (
                        <Tag style={{marginInlineEnd: 0, whiteSpace: "nowrap"}}>
                            {fmtInt(inactive)} {plural(inactive, "не учитывается", "не учитываются", "не учитываются")}
                        </Tag>
                    )}
                    <Tooltip
                        overlayStyle={{maxWidth: 460}}
                        title={
                            <>
                                {list.map((f, i) => {
                                    const lt = isNewest ? latestByCode[f.Code] : null;
                                    return (
                                        <div key={`${f.Code}-${i}`} style={{...numStyle, opacity: f.Inactive ? 0.55 : 1}}>
                                            {f.Code}{f.Shop ? ` → ${f.Shop}` : ""}: {fmtInt(f.Rows)}
                                            {f.Inactive && " · не учитывается"}
                                            {lt?.Stale && (
                                                <span style={{color: "#ffd666"}}>
                                                    {" "}· не меняется с {fmtDay(lt.UnchangedSince)}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                                {inactive > 0 && (
                                    <div style={{opacity: 0.75, marginTop: 6}}>
                                        Не учитывается — магазин отмечен неактивным (настройка «Неактивные магазины»).
                                    </div>
                                )}
                            </>
                        }
                    >
                        <span tabIndex={0} style={{...numStyle, cursor: "help", borderBottom: "1px dotted rgba(255,255,255,0.45)"}}>
                            {list.length}
                        </span>
                    </Tooltip>
                </Flex>
            );
        },
    },
];

export default function AllistCompare() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const loadSeq = useRef(0);
    const [loadError, setLoadError] = useState(false);

    // Выбранные даты "YYYY-MM-DD"; undefined — пользователь не выбирал: берём две последние копии.
    const [pickA, setPickA] = useState(undefined);
    const [pickB, setPickB] = useState(undefined);
    const [opts, setOpts] = useState({zoro: true, warehouse: true, zorofresh: false});
    const [submitting, setSubmitting] = useState(false);
    const [cancelling, setCancelling] = useState("");
    const [snapshotReq, setSnapshotReq] = useState(false);

    // Неактивные магазины: draft — несохранённый выбор пользователя (null — правок нет, показываем сохранённое
    // с сервера; фоновый опрос правки не затирает).
    const [inactiveDraft, setInactiveDraft] = useState(null);
    const [savingSettings, setSavingSettings] = useState(false);

    // Смещение часов сервера относительно браузера (мс): таймеры панели считаем по времени сервера.
    const [serverOffset, setServerOffset] = useState(0);
    const offsetRef = useRef(null);

    // Опрос: таймер следующего запроса, нужен ли частый опрос, текущий запрос.
    const pollTimer = useRef(0);
    const pollWanted = useRef(false);
    const abortRef = useRef(null);
    const loadRef = useRef(null);
    // Проверка копии, запущенная кнопкой: {checkedAt — время прошлой проверки, until} — ждём её итога.
    const snapshotWatch = useRef(null);

    // Задача, за которой следит живая панель, и итог задачи, закончившейся у нас на глазах.
    const [watchId, setWatchId] = useState("");
    const [seenData, setSeenData] = useState(null);
    const [finished, setFinished] = useState(null);
    const notifiedId = useRef("");
    const cancelledByMe = useRef(new Set());
    // Отмена запрошена, а задача ещё идёт (останавливается после текущего шага): id -> статус на момент отмены.
    const [cancelPending, setCancelPending] = useState({});
    const [notifyApi, notifyHolder] = notification.useNotification();
    const [msgApi, msgHolder] = message.useMessage();
    const msgRef = useRef(msgApi);
    msgRef.current = msgApi;

    const scheduleNext = useCallback((failed = false) => {
        clearTimeout(pollTimer.current);
        pollTimer.current = 0;
        let delay = failed ? POLL_RETRY_MS : pollWanted.current ? POLL_MS : IDLE_POLL_MS;
        if (document.visibilityState === "hidden") delay = Math.max(delay, HIDDEN_POLL_MS);
        pollTimer.current = setTimeout(() => loadRef.current?.(true), delay);
    }, []);

    // silent — фоновое обновление при опросе: без индикатора загрузки в таблице.
    const load = useCallback((silent = false) => {
        clearTimeout(pollTimer.current);
        pollTimer.current = 0;
        // Новый запрос заменяет недождавшийся старый — одновременно висит не больше одного.
        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        // Ответы могут прийти не по порядку — применяем только последний запрос.
        const seq = ++loadSeq.current;
        const token = localStorage.getItem("token");
        if (!silent) setLoading(true);
        const sentAt = Date.now();
        let failed = false;
        axios.get(Apiurl + "/allistcompare/overview?" + new URLSearchParams({token}), {timeout: LOAD_TIMEOUT_MS, signal: ctrl.signal})
            .then((res) => {
                if (seq !== loadSeq.current) return;
                const d = res.data && typeof res.data === "object" ? res.data : {};
                const src = d.Source || {};

                // Итог проверки, запущенной кнопкой: проверка не идёт и время последней проверки сменилось.
                const watch = snapshotWatch.current;
                if (watch) {
                    if (!src.Running && (src.CheckedAt || "") !== watch.checkedAt) {
                        snapshotWatch.current = null;
                        if (src.Error) msgRef.current.error(`Копия не снята: ${src.Error}`, 10);
                        else if (src.Result) msgRef.current.info(`Проверка завершена: ${src.Result}`, 8);
                    } else if (Date.now() > watch.until) {
                        snapshotWatch.current = null;
                    }
                }

                pollWanted.current = !!d.Running || (d.Jobs || []).some(isActive) || !!src.Running || !!snapshotWatch.current;
                const serverMs = Date.parse(d.ServerTime || "");
                if (!Number.isNaN(serverMs)) {
                    const offset = serverMs - (sentAt + Date.now()) / 2;
                    // Мелкие колебания (задержка сети) игнорируем, чтобы таймеры не дёргались.
                    if (offsetRef.current === null || Math.abs(offset - offsetRef.current) > 1000) {
                        offsetRef.current = offset;
                        setServerOffset(offset);
                    }
                }
                setData(d);
                setLoadError(false);
            })
            .catch((err) => {
                if (seq !== loadSeq.current || axios.isCancel?.(err)) return;
                failed = true;
                setLoadError(true);
                if (err?.response?.status === 404) {
                    // 404 бывает и при истёкшей сессии, и если сервер ещё без этого раздела — проверяем сессию.
                    axios.get(Apiurl + "/user/auth/check?" + new URLSearchParams({token})).catch((e) => {
                        if (e?.response?.status === 404) {
                            localStorage.setItem("token", "");
                            window.location.replace("/login");
                        }
                    });
                }
            })
            .finally(() => {
                // Следующий опрос планирует только последний запрос.
                if (seq !== loadSeq.current) return;
                setLoading(false);
                scheduleNext(failed);
            });
    }, [scheduleNext]);

    useEffect(() => {
        loadRef.current = load;
        load();
        return () => {
            // Уход со страницы: останавливаем опрос и игнорируем запоздавшие ответы.
            loadRef.current = null;
            loadSeq.current += 1;
            clearTimeout(pollTimer.current);
            pollTimer.current = 0;
            abortRef.current?.abort();
        };
    }, [load]);

    // Вкладка вернулась на передний план — сразу обновляем; ушла в фон — опрос переходит на редкий.
    useEffect(() => {
        const onVisibility = () => {
            if (document.visibilityState === "visible") loadRef.current?.(true);
            else if (pollTimer.current) scheduleNext();
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
    }, [scheduleNext]);

    const jobs = data?.Jobs || [];
    const running = !!data?.Running || jobs.some(isActive);
    const config = data?.Config || {};
    const source = data?.Source || {};
    const snapRunning = !!source.Running;

    // Настройка «Неактивные магазины».
    const settings = data?.Settings || {};
    const savedInactive = Array.isArray(settings.InactiveShops) ? settings.InactiveShops : [];
    const shops = Array.isArray(data?.Shops) ? data.Shops : null;
    const latest = Array.isArray(data?.Latest) ? data.Latest : [];
    const inactiveValue = inactiveDraft ?? savedInactive;
    const settingsDirty = inactiveDraft !== null && !sameSet(inactiveDraft, savedInactive);

    // Пришли новые данные. Задача панели закончилась между опросами — переключаем панель на итог
    // прямо в этом рендере (без промежуточного кадра, где панели нет).
    if (data !== seenData) {
        setSeenData(data);
        if (data) {
            const current = pickPanelJob(jobs);
            if (watchId && (!current || current.Id !== watchId)) {
                const job = jobs.find((j) => j.Id === watchId);
                if (job && !isActive(job)) setFinished(job);
            }
            const nextId = current ? current.Id : "";
            if (nextId !== watchId) setWatchId(nextId);

            // «Отмена…» держим, пока статус задачи не изменился.
            const pendingIds = Object.keys(cancelPending);
            const keep = pendingIds.filter((id) => jobs.find((j) => j.Id === id)?.Status === cancelPending[id]);
            if (keep.length !== pendingIds.length) {
                setCancelPending(Object.fromEntries(keep.map((id) => [id, cancelPending[id]])));
            }
        }
    }

    // Уведомление об окончании задачи, за которой следила панель.
    useEffect(() => {
        const job = finished;
        if (!job || notifiedId.current === job.Id) return;
        notifiedId.current = job.Id;
        const key = "allistcompare-" + job.Id;
        const dur = jobDurationSec(job);
        const s = job.Stats || {};
        if (job.Status === "done") {
            notifyApi.success({
                key,
                message: "Сравнение All List готово",
                description:
                    `${fmtPair(job.DateA, job.DateB)}: удалено ${fmtInt(s.Deleted)} ASIN, обнулено ${fmtInt(s.Zeroed)} ASIN.` +
                    (dur !== null ? ` Сформировано за ${fmtDuration(dur)}.` : ""),
                btn: job.HasFile ? (
                    <Button type="primary" size="small" icon={<DownloadOutlined/>}
                            href={downloadHref(job)} target="_blank" rel="noreferrer">
                        Скачать Excel
                    </Button>
                ) : undefined,
                duration: 12,
                placement: "bottomRight",
            });
        } else if (job.Status === "failed") {
            notifyApi.error({
                key,
                message: "Сравнение не сформировано",
                description: job.Message || "Ошибка при формировании сравнения",
                duration: 12,
                placement: "bottomRight",
            });
        } else if (job.Status === "cancelled" && !cancelledByMe.current.has(job.Id)) {
            notifyApi.info({
                key,
                message: "Формирование сравнения отменено",
                description: job.Message || undefined,
                duration: 8,
                placement: "bottomRight",
            });
        }
    }, [finished, notifyApi]);

    // Копии и выбор дат.
    const snapshots = sortSnapshots(data?.Snapshots);
    const snapDays = new Set(snapshots.map((s) => s.Date));
    const enoughSnapshots = snapshots.length >= 2;
    const dateA = pickA === undefined ? snapshots[1]?.Date || null : pickA;
    const dateB = pickB === undefined ? snapshots[0]?.Date || null : pickB;
    // В панелях месяцев/лет доступны те, где есть хоть одна копия (иначе rc-picker блокирует
    // месяц/год по его первому и последнему дню, и из заголовка календаря не вернуться к дням).
    const disabledDate = (d, info) => {
        const type = info?.type;
        if (type === "month" || type === "year") {
            const prefix = d.format(type === "month" ? "YYYY-MM" : "YYYY");
            return !snapshots.some((s) => s.Date.startsWith(prefix));
        }
        if (type && type !== "date") return false;
        return !snapDays.has(d.format("YYYY-MM-DD"));
    };

    let dateError = "";
    if (enoughSnapshots) {
        if (!dateA || !dateB) dateError = "Выберите обе даты";
        else if (!snapDays.has(dateA)) dateError = `Копии за ${fmtDay(dateA)} больше нет — выберите другую дату`;
        else if (!snapDays.has(dateB)) dateError = `Копии за ${fmtDay(dateB)} больше нет — выберите другую дату`;
        else if (dateA >= dateB) dateError = "Ранняя дата должна быть раньше поздней";
    }

    const available = (key) => {
        const opt = OPTIONS.find((o) => o.key === key);
        return !(opt && config[opt.cfg] === false);
    };
    const effective = {
        zoro: opts.zoro && available("zoro"),
        warehouse: opts.warehouse && available("warehouse"),
    };
    effective.zorofresh = opts.zorofresh && effective.zoro;

    const canSubmit = !!data && !running && enoughSnapshots && !dateError;

    const submit = () => {
        if (!canSubmit) return;
        const params = {token: localStorage.getItem("token"), a: dateA, b: dateB};
        ["zoro", "warehouse", "zorofresh"].forEach((k) => (params[k] = effective[k] ? "1" : "0"));

        setSubmitting(true);
        axios.post(Apiurl + "/allistcompare/jobs?" + new URLSearchParams(params))
            .then((res) => {
                if (res.data?.Status) {
                    const job = res.data.Job;
                    if (job) {
                        setData((d) => (d ? {...d, Running: true, Jobs: [job, ...(d.Jobs || []).filter((j) => j.Id !== job.Id)]} : d));
                    }
                    setFinished(null);
                    pollWanted.current = true;
                    msgApi.success("Сравнение поставлено в очередь — ход формирования виден на панели вверху.", 6);
                    load(true);
                } else {
                    msgApi.warning(res.data?.Message || "Не удалось запустить сравнение", 8);
                }
            })
            .catch((err) => {
                const msg = err?.response?.data?.Message;
                if (err?.response?.status === 409) {
                    msgApi.warning(msg || "Сравнение уже формируется — дождитесь окончания", 8);
                    load(true);
                } else {
                    msgApi.error(msg || "Не удалось запустить сравнение", 10);
                }
            })
            .finally(() => setSubmitting(false));
    };

    const takeSnapshot = () => {
        const checkedAt = data?.Source?.CheckedAt || "";
        setSnapshotReq(true);
        axios.post(Apiurl + "/allistcompare/snapshot?" + new URLSearchParams({token: localStorage.getItem("token")}))
            .then((res) => {
                if (res.data?.Status) {
                    msgApi.success(res.data.Message || "Проверка запущена", 6);
                    // Проверка идёт в фоне — часто опрашиваем, пока не появится её итог.
                    snapshotWatch.current = {checkedAt, until: Date.now() + SNAPSHOT_WATCH_MS};
                    pollWanted.current = true;
                } else {
                    msgApi.warning(res.data?.Message || "Не удалось запустить проверку", 8);
                }
                load(true);
            })
            .catch((err) => {
                const msg = err?.response?.data?.Message;
                if (err?.response?.status === 409) {
                    msgApi.warning(msg || "Проверка уже идёт", 8);
                    load(true);
                } else {
                    msgApi.error(msg || "Не удалось запустить проверку", 10);
                }
            })
            .finally(() => setSnapshotReq(false));
    };

    const cancel = (id) => {
        cancelledByMe.current.add(id);
        setCancelling(id);
        axios.post(Apiurl + "/allistcompare/jobs/cancel?" + new URLSearchParams({token: localStorage.getItem("token"), id}))
            .then((res) => {
                if (res.data?.Status) {
                    msgApi.success(res.data.Message || "Формирование сравнения отменено", 6);
                    // Задача останавливается после текущего шага — до смены статуса показываем «отмена…».
                    const job = (data?.Jobs || []).find((j) => j.Id === id);
                    if (job && isActive(job)) setCancelPending((p) => ({...p, [id]: job.Status}));
                } else {
                    msgApi.warning(res.data?.Message || "Не удалось отменить сравнение", 8);
                }
                load(true);
            })
            .catch((err) => msgApi.error(err?.response?.data?.Message || "Не удалось отменить сравнение", 10))
            .finally(() => setCancelling(""));
    };

    // Выбор совпал с сохранённым — правок нет (снова показываем то, что пришло с сервера).
    const changeInactive = (ids) => setInactiveDraft(sameSet(ids, savedInactive) ? null : ids);
    const addInactive = (id) => {
        if (!id || inactiveValue.includes(id)) return;
        changeInactive([...inactiveValue, id]);
    };

    const saveSettings = () => {
        if (!settingsDirty || savingSettings) return;
        setSavingSettings(true);
        axios.post(Apiurl + "/allistcompare/settings?" + new URLSearchParams({token: localStorage.getItem("token")}),
            {InactiveShops: inactiveDraft})
            .then((res) => {
                if (res.data?.Status) {
                    const saved = res.data.Settings;
                    if (saved) setData((d) => (d ? {...d, Settings: saved} : d));
                    setInactiveDraft(null);
                    msgApi.success(res.data.Message || "Сохранено", 8);
                    load(true);
                } else {
                    msgApi.warning(res.data?.Message || "Не удалось сохранить настройку", 8);
                }
            })
            .catch((err) => msgApi.error(err?.response?.data?.Message || "Не удалось сохранить настройку", 10))
            .finally(() => setSavingSettings(false));
    };

    const renderStatus = (r) => {
        const st = STATUS[r.Status] || {color: "default", label: r.Status || "—"};
        const tag = <Tag color={st.color} icon={st.icon} style={{marginInlineEnd: 0}}>{st.label}</Tag>;

        if (r.Status === "running" || r.Status === "queued") {
            const total = Number(r.Progress?.Total) || 0;
            const done = Number(r.Progress?.Done) || 0;
            return (
                <Flex vertical gap="4px" style={{minWidth: 170}}>
                    <Flex gap="6px" align="center" wrap="wrap">
                        {tag}
                        {r.StageLabel && <Text type="secondary" style={{fontSize: 12}}>{r.StageLabel}</Text>}
                    </Flex>
                    {r.Status === "running" && total > 0 && (
                        <Tooltip title={`${fmtInt(done)} из ${fmtInt(total)}`}>
                            <div>
                                <Progress
                                    size="small"
                                    status="active"
                                    style={{margin: 0}}
                                    percent={Math.min(100, Math.floor((done / total) * 100))}
                                />
                            </div>
                        </Tooltip>
                    )}
                </Flex>
            );
        }
        if (r.Status === "done") {
            return fmtMsk(r.FinishedAt) ? <Tooltip title={`Готов: ${fmtMsk(r.FinishedAt)}`}>{tag}</Tooltip> : tag;
        }
        return r.Message ? <Tooltip overlayStyle={{maxWidth: 560}} title={r.Message}>{tag}</Tooltip> : tag;
    };

    const renderCount = (r, asins, rows) =>
        r.Status === "done" ? (
            <Text style={numStyle}><b>{fmtInt(asins)}</b> ASIN / {fmtRows(rows)}</Text>
        ) : (
            <Text type="secondary">—</Text>
        );

    const renderSections = (r) => {
        const s = r.Stats || {};
        const done = r.Status === "done";
        const tags = [];
        if (r.WithZoro) {
            tags.push(
                <Tooltip
                    key="zoro"
                    overlayStyle={{maxWidth: 420}}
                    title={
                        <>
                            {done && (
                                <div>
                                    найдено {fmtInt(s.ZoroFound)} · не найдено {fmtInt(s.ZoroNotFound)} · сбой {fmtInt(s.ZoroFailed)} ·
                                    из кэша {fmtInt(s.ZoroCached)}
                                </div>
                            )}
                            {r.ZoroFresh && <div>Данные Zoro запрошены заново, без кэша</div>}
                            {done && <div style={{opacity: 0.75, marginTop: 4}}>{ZORO_FAILED_HINT}</div>}
                            {!done && !r.ZoroFresh && <div>Данные Zoro</div>}
                        </>
                    }
                >
                    <Tag color={done && Number(s.ZoroFailed) > 0 ? "orange" : "purple"} style={{marginInlineEnd: 0}}>
                        Zoro{r.ZoroFresh ? " · без кэша" : ""}
                    </Tag>
                </Tooltip>
            );
        }
        if (r.WithWarehouse) {
            tags.push(
                <Tooltip
                    key="wp"
                    title={
                        done
                            ? `Найдено в Warehouse Portal: ${fmtRows(s.WarehouseMatched)}, в наличии: ${fmtInt(s.WarehouseInStock)}`
                            : "Warehouse Portal"
                    }
                >
                    <Tag color="geekblue" style={{marginInlineEnd: 0}}>WP</Tag>
                </Tooltip>
            );
        }
        return tags.length > 0 ? <Flex gap="4px" wrap="wrap">{tags}</Flex> : <Text type="secondary">—</Text>;
    };

    const columns = [
        {
            title: "Запущен",
            dataIndex: "CreatedAt",
            render: (v) => <Text strong style={{whiteSpace: "nowrap"}}>{fmtMsk(v) || "—"}</Text>,
        },
        {
            title: "Сравнение",
            render: (_, r) => {
                const excluded = Array.isArray(r.Excluded) ? r.Excluded.filter(Boolean) : [];
                return (
                    <Flex gap="6px" align="center" wrap="wrap">
                        <span style={{whiteSpace: "nowrap"}}>{fmtPair(r.DateA, r.DateB)}</span>
                        {excluded.length > 0 && (
                            <Tooltip overlayStyle={{maxWidth: 460}} title={`Не учитывались неактивные магазины: ${excluded.join(", ")}`}>
                                <Tag tabIndex={0} style={{marginInlineEnd: 0, cursor: "help", whiteSpace: "nowrap"}}>
                                    без {fmtInt(excluded.length)} магаз.
                                </Tag>
                            </Tooltip>
                        )}
                    </Flex>
                );
            },
        },
        {
            title: "Статус",
            dataIndex: "Status",
            render: (_, r) => renderStatus(r),
        },
        {
            title: "Удалено",
            render: (_, r) => renderCount(r, r.Stats?.Deleted, r.Stats?.DeletedRows),
        },
        {
            title: "Обнулено",
            render: (_, r) => renderCount(r, r.Stats?.Zeroed, r.Stats?.ZeroedRows),
        },
        {
            title: "Разделы",
            render: (_, r) => renderSections(r),
        },
        {
            title: "Запустил",
            dataIndex: "CreatedBy",
            render: (v) => (v ? <span style={{whiteSpace: "nowrap"}}>{v}</span> : <Text type="secondary">—</Text>),
        },
        {
            title: "Длительность",
            render: (_, r) => {
                const dur = isActive(r) ? null : jobDurationSec(r);
                return dur !== null ? <span style={numStyle}>{fmtDuration(dur)}</span> : <Text type="secondary">—</Text>;
            },
        },
        {
            title: "",
            align: "right",
            render: (_, r) => {
                const pendingCancel = isActive(r) && !!cancelPending[r.Id];
                const warnings = Array.isArray(r.Warnings) ? r.Warnings : [];
                const failed = r.Status === "failed";
                const showDetails = warnings.length > 0 || (failed && !!r.Message);
                return (
                    <Flex gap="6px" justify="flex-end" align="center" wrap="wrap">
                        {showDetails && (
                            <Tooltip
                                overlayStyle={{maxWidth: 560}}
                                title={
                                    <>
                                        {failed && r.Message && (
                                            <div style={{marginBottom: warnings.length > 0 ? 8 : 0}}>Ошибка: {r.Message}</div>
                                        )}
                                        {warnings.map((x, i) => (
                                            <div key={i} style={{marginBottom: 6}}>• {x}</div>
                                        ))}
                                    </>
                                }
                            >
                                <Tag
                                    tabIndex={0}
                                    color={failed ? "red" : "orange"}
                                    icon={failed ? <InfoCircleOutlined/> : <WarningOutlined/>}
                                    style={{marginInlineEnd: 0, cursor: "help"}}
                                >
                                    {failed ? "подробнее" : warnings.length}
                                </Tag>
                            </Tooltip>
                        )}
                        {r.HasFile && (
                            <Tooltip title={r.Filename || undefined}>
                                <Button icon={<DownloadOutlined/>} href={downloadHref(r)} target="_blank" rel="noreferrer">
                                    Скачать
                                </Button>
                            </Tooltip>
                        )}
                        {r.Status === "done" && !r.HasFile && (
                            <Text type="secondary" style={{fontSize: 12, maxWidth: 220, textAlign: "right"}}>
                                {FILE_GONE_HINT}
                            </Text>
                        )}
                        {pendingCancel && (
                            <Tag icon={<SyncOutlined spin/>} color="warning" style={{marginInlineEnd: 0}}>отмена…</Tag>
                        )}
                        {pendingCancel && (
                            <Tooltip title={CANCEL_PENDING_HINT}>
                                <Button danger icon={<StopOutlined/>} disabled>Отменить</Button>
                            </Tooltip>
                        )}
                        {isActive(r) && !pendingCancel && (
                            <Popconfirm
                                title="Отменить формирование сравнения?"
                                okText="Отменить"
                                okButtonProps={{danger: true}}
                                cancelText="Нет"
                                onConfirm={() => cancel(r.Id)}
                            >
                                <Button danger icon={<StopOutlined/>} loading={cancelling === r.Id}
                                        disabled={cancelling !== "" && cancelling !== r.Id}>
                                    Отменить
                                </Button>
                            </Popconfirm>
                        )}
                    </Flex>
                );
            },
        },
    ];

    const activeJob = pickPanelJob(jobs);
    const finishedJob = finished ? jobs.find((j) => j.Id === finished.Id) || finished : null;
    const panelJob = activeJob || (finishedJob && !isActive(finishedJob) ? finishedJob : null);

    if (loadError && !data) {
        return (
            <div style={wrapStyle}>
                <Flex vertical align="center">
                    <Alert
                        type="error"
                        showIcon
                        style={cardStyle}
                        message="Не удалось загрузить данные сравнения All List"
                        action={<Button onClick={() => load()} loading={loading}>Повторить</Button>}
                    />
                </Flex>
            </div>
        );
    }

    // Сводка по копиям.
    const keepDays = Number(data?.KeepDays) || 0;
    const newestDay = snapshots[0]?.Date;
    const oldestDay = snapshots[snapshots.length - 1]?.Date;
    let summary = `Сохранено копий: ${fmtInt(snapshots.length)}`;
    if (snapshots.length === 1) summary += ` (за ${fmtDay(newestDay)})`;
    else if (snapshots.length > 1) summary += ` (с ${fmtDay(oldestDay)} по ${fmtDay(newestDay)})`;
    summary += keepDays > 0 ? `, хранятся ${fmtInt(keepDays)} ${plural(keepDays, "день", "дня", "дней")}.` : ".";

    const notEnoughText =
        snapshots.length === 0
            ? "Для сравнения нужны копии минимум за две даты. Копий пока нет — первая появится после очередной загрузки All List в manager.enjoytool.net."
            : `Для сравнения нужны копии минимум за две даты. Первая копия: ${fmtDay(newestDay)} — следующая появится после очередной загрузки All List в manager.enjoytool.net.`;

    const sourceBlock = config.Source === false ? (
        <Alert
            type="warning"
            showIcon
            message="Не настроен доступ к manager.enjoytool.net (ALLIST_MONGO) — копии не снимаются."
        />
    ) : (
        <Flex vertical gap="8px">
            <Text>
                Последняя загрузка в manager.enjoytool.net:{" "}
                {isZeroTime(source.LoggedAt) ? (
                    <Text type="secondary">нет данных</Text>
                ) : (
                    <b style={numStyle}>
                        {fmtMsk(source.LoggedAt)}
                        {Number(source.Rows) > 0 && !source.Incomplete ? ` · ${fmtRows(source.Rows)}` : ""}
                    </b>
                )}
            </Text>
            {source.Incomplete && (
                <Alert
                    type="warning"
                    showIcon
                    message="Загрузка в manager.enjoytool.net идёт или оборвалась — копия будет снята после её завершения."
                />
            )}
            {source.Error && <Alert type="error" showIcon message={source.Error}/>}
            {!isZeroTime(source.CheckedAt) && (
                <Text type="secondary" style={{fontSize: 13}}>
                    Последняя проверка {fmtMskShort(source.CheckedAt)}{source.Result ? `: ${source.Result}` : ""}
                </Text>
            )}
        </Flex>
    );

    // Файлы самой новой копии: по коду — для таблицы копий; по магазину — самая ранняя дата, с которой файл не меняется.
    const latestByCode = {};
    const staleSinceByShop = {};
    latest.forEach((f) => {
        if (!f || !f.Code) return;
        latestByCode[f.Code] = f;
        if (f.Stale && f.ShopId && !isZeroTime(f.UnchangedSince)) {
            const prev = staleSinceByShop[f.ShopId];
            if (!prev || f.UnchangedSince < prev) staleSinceByShop[f.ShopId] = f.UnchangedSince;
        }
    });
    const snapshotColumns = makeSnapshotColumns(latestByCode, snapshots[0]?.Date);
    // Не меняются несколько загрузок подряд, но не отмечены неактивными — подсказка.
    const staleFiles = latest.filter((f) => f && f.Stale && !f.Inactive);
    const staleUnmapped = staleFiles.filter((f) => !f.ShopId);

    const shopById = new Map((shops || []).map((s) => [s.Id, s]));
    const shopOptions = (shops || []).map((s) => ({value: s.Id, label: shopLabel(s), staleSince: staleSinceByShop[s.Id]}));
    // Отмеченный магазин, которого уже нет в списке, — показываем его id, чтобы его можно было снять.
    inactiveValue.forEach((id) => {
        if (!shopById.has(id)) shopOptions.push({value: id, label: `${id} (магазин не найден)`});
    });
    // Названия сохранённых неактивных магазинов — в порядке списка магазинов (по имени).
    const savedInactiveSet = new Set(savedInactive);
    const inactiveNames = [
        ...(shops || []).filter((s) => savedInactiveSet.has(s.Id)).map((s) => s.Name || s.Id),
        ...savedInactive.filter((id) => !shopById.has(id)),
    ];

    const settingsCard = (
        <Card style={cardStyle} styles={{body: {padding: 28}}}>
            <Flex vertical gap="16px">
                <Flex vertical gap="6px" style={{maxWidth: 760}}>
                    <Title level={4} style={{margin: 0}}>Неактивные магазины</Title>
                    <Text type="secondary">
                        All List этих магазинов не учитывается: в сравнении (на обеих датах) и в отчёте «Все ASIN с продажами»
                        (статус «магазин неактивен»). Копии хранятся целиком — настройка действует на все отчёты,
                        сформированные после её изменения, в том числе за прошлые даты.
                    </Text>
                </Flex>

                {!data ? (
                    <Flex justify="center" style={{padding: 16}}><Spin/></Flex>
                ) : !shops ? (
                    <Alert type="info" showIcon message="Сервер не вернул список магазинов — настройка недоступна."/>
                ) : (
                    <>
                        <Flex vertical gap="8px">
                            <Select
                                mode="multiple"
                                allowClear
                                style={{width: "100%", maxWidth: 760}}
                                placeholder="Все магазины активны — выберите неактивные"
                                options={shopOptions}
                                value={inactiveValue}
                                onChange={changeInactive}
                                disabled={savingSettings}
                                optionFilterProp="label"
                                notFoundContent="Магазин не найден"
                                optionRender={(o) => (
                                    <Flex justify="space-between" align="center" gap="8px">
                                        <span style={{overflow: "hidden", textOverflow: "ellipsis"}}>{o.data.label}</span>
                                        {o.data.staleSince && (
                                            <Tag color="gold" style={{marginInlineEnd: 0, flexShrink: 0}}>
                                                не меняется с {fmtDayShort(o.data.staleSince)}
                                            </Tag>
                                        )}
                                    </Flex>
                                )}
                            />
                            <Flex gap="8px 12px" align="center" wrap="wrap">
                                <Button type="primary" icon={<SaveOutlined/>} onClick={saveSettings}
                                        loading={savingSettings} disabled={!settingsDirty}>
                                    Сохранить
                                </Button>
                                <Button onClick={() => setInactiveDraft(null)} disabled={!settingsDirty || savingSettings}>
                                    Отменить
                                </Button>
                                {settingsDirty && <Text type="warning" style={{fontSize: 13}}>Изменения не сохранены</Text>}
                            </Flex>
                            {!isZeroTime(settings.UpdatedAt) && (
                                <Text type="secondary" style={{fontSize: 13}}>
                                    Изменено {fmtMsk(settings.UpdatedAt)}{settings.UpdatedBy ? ` (${settings.UpdatedBy})` : ""}
                                </Text>
                            )}
                        </Flex>

                        {staleFiles.length > 0 && (
                            <Alert
                                type="warning"
                                showIcon
                                message={
                                    <span>
                                        Не меняются в последних загрузках:{" "}
                                        {staleFiles.map((f, i) => {
                                            const picked = !!f.ShopId && inactiveValue.includes(f.ShopId);
                                            let name;
                                            if (!f.ShopId) {
                                                name = (
                                                    <Tooltip title="Файл не сопоставлен ни с одним магазином">
                                                        <Text strong style={{cursor: "help"}}>{fileLabel(f)}</Text>
                                                    </Tooltip>
                                                );
                                            } else if (picked) {
                                                name = (
                                                    <Tooltip title={settingsDirty ? "Отмечен — нажмите «Сохранить»" : "Отмечен неактивным"}>
                                                        <Tag color="green" icon={<CheckOutlined/>} style={{marginInlineEnd: 0}}>
                                                            {fileLabel(f)}
                                                        </Tag>
                                                    </Tooltip>
                                                );
                                            } else {
                                                name = (
                                                    <Tooltip title="Отметить неактивным">
                                                        <Tag
                                                            color="gold"
                                                            icon={<PlusOutlined/>}
                                                            role="button"
                                                            tabIndex={0}
                                                            style={{marginInlineEnd: 0, cursor: savingSettings ? "default" : "pointer"}}
                                                            onClick={() => !savingSettings && addInactive(f.ShopId)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter" || e.key === " ") {
                                                                    e.preventDefault();
                                                                    if (!savingSettings) addInactive(f.ShopId);
                                                                }
                                                            }}
                                                        >
                                                            {fileLabel(f)}
                                                        </Tag>
                                                    </Tooltip>
                                                );
                                            }
                                            return (
                                                <React.Fragment key={`${f.Code}-${i}`}>
                                                    {i > 0 && "; "}
                                                    {name}
                                                    {" "}— с {fmtDay(f.UnchangedSince)}, {fmtUploads(f.UnchangedCount)}
                                                </React.Fragment>
                                            );
                                        })}
                                        .
                                        {staleFiles.some((f) => f.ShopId && !inactiveValue.includes(f.ShopId)) && " Если магазин неактивен — отметьте его выше."}
                                        {staleUnmapped.length > 0 &&
                                            ` Файл ${staleUnmapped.map((f) => f.Code).join(", ")} не сопоставлен с магазином — отметить его нельзя, его строки учитываются (сопоставление задаётся на сервере).`}
                                    </span>
                                }
                            />
                        )}
                    </>
                )}
            </Flex>
        </Card>
    );

    return (
        <div style={wrapStyle}>
            {notifyHolder}
            {msgHolder}
            <Flex vertical align="center" gap="24px">
                {panelJob && (
                    <AsinReportProgress
                        job={panelJob}
                        typical={data?.Typical}
                        serverOffset={serverOffset}
                        periodLabel={fmtPair(panelJob.DateA, panelJob.DateB)}
                        downloadHref={downloadHref}
                        onCancel={cancel}
                        cancelling={cancelling}
                        cancelPending={!!cancelPending[panelJob.Id]}
                        onDismiss={() => setFinished(null)}
                        title="Сравнение All List"
                        counters={compareCounters}
                    />
                )}

                <Card style={cardStyle} styles={{body: {padding: 28}}}>
                    <Flex vertical gap="16px">
                        <Flex vertical gap="6px" style={{maxWidth: 760}}>
                            <Title level={4} style={{margin: 0}}>Копии All List</Title>
                            <Text type="secondary">
                                All Listings всех магазинов каждый будний день загружаются в manager.enjoytool.net, а он хранит
                                только последнюю загрузку. Поэтому после каждой загрузки сервер сохраняет её копию за этот день —
                                по копиям и делается сравнение.
                            </Text>
                        </Flex>

                        {!data ? (
                            <Flex justify="center" style={{padding: 16}}><Spin/></Flex>
                        ) : (
                            <>
                                {sourceBlock}
                                {config.Scheduler === false && (
                                    <Text type="secondary">
                                        <InfoCircleOutlined/> Автоматическое снятие копий выключено на этом сервере — используйте кнопку.
                                    </Text>
                                )}
                                <Text>{summary}</Text>
                                <Flex gap="8px 12px" align="center" wrap="wrap">
                                    <Tooltip title={config.Source === false ? "Нет доступа к manager.enjoytool.net" : undefined}>
                                        <Button
                                            icon={<CameraOutlined/>}
                                            onClick={takeSnapshot}
                                            loading={snapRunning || snapshotReq}
                                            disabled={config.Source === false || snapRunning}
                                        >
                                            {snapRunning ? "снимается копия…" : "Проверить и снять копию"}
                                        </Button>
                                    </Tooltip>
                                    <Text type="secondary" style={{fontSize: 12, maxWidth: 640}}>
                                        Проверяет последнюю загрузку в manager.enjoytool.net и сохраняет её копию за этот день,
                                        если такой копии ещё нет или загрузка новее.
                                    </Text>
                                </Flex>
                                {snapshots.length > 0 && (
                                    <Collapse
                                        size="small"
                                        items={[
                                            {
                                                key: "snapshots",
                                                label: `Сохранённые копии (${fmtInt(snapshots.length)})`,
                                                children: (
                                                    <Table
                                                        rowKey="Date"
                                                        size="small"
                                                        columns={snapshotColumns}
                                                        dataSource={snapshots}
                                                        pagination={{pageSize: 10, hideOnSinglePage: true, showSizeChanger: false}}
                                                        scroll={{x: true}}
                                                    />
                                                ),
                                            },
                                        ]}
                                    />
                                )}
                            </>
                        )}
                    </Flex>
                </Card>

                {settingsCard}

                <Card style={cardStyle} styles={{body: {padding: 28}}}>
                    <Flex vertical gap="20px">
                        <Flex vertical gap="6px" style={{maxWidth: 760}}>
                            <Title level={4} style={{margin: 0}}>Сравнение</Title>
                            <Text type="secondary">
                                Выберите две даты, за которые есть копии All List, — сформируется один Excel-файл с двумя листами.
                                Формируется в фоне.
                            </Text>
                            {inactiveNames.length > 0 && (
                                <Text>
                                    <StopOutlined style={{color: "#faad14"}}/> Не учитываются неактивные магазины:{" "}
                                    {inactiveNames.join(", ")}.
                                </Text>
                            )}
                        </Flex>

                        {data && !enoughSnapshots && <Alert type="info" showIcon message={notEnoughText}/>}

                        <Flex vertical gap="6px">
                            <Flex gap="12px 24px" wrap="wrap" align="flex-end">
                                <Flex vertical gap="6px">
                                    <Text strong>Ранняя дата</Text>
                                    <DatePicker
                                        style={{width: 180}}
                                        format="DD.MM.YYYY"
                                        placeholder="Ранняя дата"
                                        allowClear={false}
                                        disabled={!enoughSnapshots}
                                        disabledDate={disabledDate}
                                        status={dateError ? "error" : undefined}
                                        value={dateA ? dayjs(dateA) : null}
                                        onChange={(d) => setPickA(d ? d.format("YYYY-MM-DD") : null)}
                                    />
                                </Flex>
                                <Text type="secondary" style={{paddingBottom: 5}}>→</Text>
                                <Flex vertical gap="6px">
                                    <Text strong>Поздняя дата</Text>
                                    <DatePicker
                                        style={{width: 180}}
                                        format="DD.MM.YYYY"
                                        placeholder="Поздняя дата"
                                        allowClear={false}
                                        disabled={!enoughSnapshots}
                                        disabledDate={disabledDate}
                                        status={dateError ? "error" : undefined}
                                        value={dateB ? dayjs(dateB) : null}
                                        onChange={(d) => setPickB(d ? d.format("YYYY-MM-DD") : null)}
                                    />
                                </Flex>
                            </Flex>
                            {dateError ? (
                                <Text type="danger" role="alert">{dateError}</Text>
                            ) : (
                                <Text type="secondary" style={{fontSize: 12}}>Доступны только даты, за которые есть копия.</Text>
                            )}
                        </Flex>

                        <Flex vertical gap="8px">
                            <Text strong>Дополнительные данные</Text>
                            <Flex gap="8px 24px" wrap="wrap" align="center">
                                {OPTIONS.map((o) => (
                                    <Tooltip key={o.key} title={available(o.key) ? o.tip : "Не настроено на сервере"}>
                                        <span>
                                            <Checkbox
                                                checked={effective[o.key]}
                                                disabled={!available(o.key)}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    // Без Zoro сбрасываем и обновление без кэша, чтобы оно не включилось само.
                                                    setOpts((p) => ({
                                                        ...p,
                                                        [o.key]: checked,
                                                        ...(o.key === "zoro" && !checked ? {zorofresh: false} : {}),
                                                    }));
                                                }}
                                            >
                                                {o.label}
                                            </Checkbox>
                                        </span>
                                    </Tooltip>
                                ))}
                                {effective.zoro && (
                                    <Tooltip title="Запросить все номера в Zoro заново, не используя сохранённые данные">
                                        <span>
                                            <Checkbox
                                                checked={effective.zorofresh}
                                                onChange={(e) => setOpts((p) => ({...p, zorofresh: e.target.checked}))}
                                            >
                                                Обновить Zoro без кэша
                                            </Checkbox>
                                        </span>
                                    </Tooltip>
                                )}
                            </Flex>
                            <Text type="secondary" style={{fontSize: 12}}>
                                Zoro: Zoro SKU, Title, Quantity, Handling time, Brand. Warehouse Portal: WP SKU и Quantity
                                (сопоставление по Zoro SKU). Колонки добавляются на оба листа.
                            </Text>
                        </Flex>

                        <Flex vertical gap="4px" style={{maxWidth: 820}}>
                            <Text strong>Листы файла</Text>
                            <Text type="secondary">
                                <Text strong>«Удалённые»</Text> — ASIN, которые на раннюю дату были хотя бы в одном магазине,
                                а на позднюю нет ни в одном. Данные — на раннюю дату.
                            </Text>
                            <Text type="secondary">
                                <Text strong>«Обнулённые»</Text> — ASIN, у которых на раннюю дату суммарное количество по всем
                                магазинам было больше 0, а на позднюю они есть, но с количеством 0 во всех магазинах. Перенос
                                между магазинами (в одном активен, в другом 0) не считается.
                            </Text>
                        </Flex>

                        <Flex gap="12px" align="center" wrap="wrap">
                            <Button type="primary" icon={<ThunderboltOutlined/>} onClick={submit}
                                    loading={submitting} disabled={!canSubmit}>
                                Сформировать
                            </Button>
                            {running && (
                                <Text type="secondary">
                                    Сравнение уже формируется — ход работы на панели вверху; дождитесь окончания или отмените его.
                                </Text>
                            )}
                        </Flex>
                    </Flex>
                </Card>

                <Card
                    style={cardStyle}
                    styles={{body: {padding: 24}}}
                    title="Сформированные сравнения"
                    extra={
                        <Flex gap="8px" align="center">
                            {running && <Tag icon={<SyncOutlined spin/>} color="processing">автообновление</Tag>}
                            <Button icon={<ReloadOutlined/>} onClick={() => load()} loading={loading}>Обновить</Button>
                        </Flex>
                    }
                >
                    {loadError && (
                        <Alert
                            type="error"
                            showIcon
                            style={{marginBottom: 12}}
                            message="Не удалось обновить данные — показаны последние загруженные"
                            action={<Button size="small" onClick={() => load()}>Повторить</Button>}
                        />
                    )}
                    <Table
                        rowKey="Id"
                        size="middle"
                        loading={loading}
                        columns={columns}
                        dataSource={jobs}
                        pagination={{pageSize: 15, hideOnSinglePage: true}}
                        locale={{emptyText: <Empty description="Пока нет сформированных сравнений"/>}}
                        scroll={{x: true}}
                    />
                </Card>
            </Flex>
        </div>
    );
}
